<template>
  <div id="app">
    <!-- Top Navigation -->
    <NavigationBar />

    <div class="container" :class="{ 'admin-container': isAdminRoute }">
      <!-- Main content area -->
      <main class="main-content" :class="{ 'admin-content': isAdminRoute }">
        <router-view></router-view>
      </main>

      <!-- Progress Tracker - hide on admin pages, home page, and auth pages -->
      <ProgressTracker v-if="showProgressTracker" />
    </div>
  </div>
</template>

<script>
import NavigationBar from './components/NavigationBar.vue'
import ProgressTracker from './components/ProgressTracker.vue'
import { Auth } from '@aws-amplify/auth'
import { onMounted, provide, ref, inject, computed } from 'vue'
import { useRoute } from 'vue-router'
import { fetchUserProgress } from './utils/progress'

export default {
  name: 'App',
  components: {
    NavigationBar,
    ProgressTracker
  },
  setup() {
    // Create a reactive reference to track whether the initial fetch is complete
    const progressInitialized = ref(false);
    
    // Get the logger
    const logger = inject('logger');
    
    // Get the current route
    const route = useRoute();
    
    // Compute whether we're on an admin route
    const isAdminRoute = computed(() => {
      return route.path.startsWith('/admin');
    });
    
    // Compute whether we're on the home route
    const isHomeRoute = computed(() => {
      return route.path === '/';
    });
    
    // Compute whether we're on authentication routes (login, signup, callback)
    const isAuthRoute = computed(() => {
      return ['/login', '/signup', '/callback'].includes(route.path);
    });
    
    // Compute whether to show the progress tracker
    const showProgressTracker = computed(() => {
      return !isAdminRoute.value && !isHomeRoute.value && !isAuthRoute.value;
    });
    
    // Provide this to child components so they know when it's safe to use progress data
    provide('progressInitialized', progressInitialized);
    
    onMounted(async () => {
      // This is the ONLY place in the app where fetchUserProgress should be called on initial load
      logger.info('App: Starting initial progress fetch');
      
      try {
        const user = await Auth.currentAuthenticatedUser();
        if (user) {
          logger.debug('User authenticated, fetching progress data');
          await fetchUserProgress();
          logger.info('Initial progress fetch complete');
        }
      } catch (error) {
        logger.warn('Not authenticated yet, skipping progress fetch', { error: error?.message });
      } finally {
        // Mark the progress as initialized, even if there was an error
        progressInitialized.value = true;
        logger.debug('Progress initialization state set to complete');
      }
    });

    return {
      progressInitialized,
      isAdminRoute,
      showProgressTracker
    };
  }
}
</script>

<style>
:root {
  --primary: #2b6cb0;
  --primary-light: #4299e1;
  --secondary: #90cdf4;
  --background: #f7fafc;
  --text: #2d3748;
  --text-light: #4a5568;
  --border: #e2e8f0;
}

body {
  margin: 0;
  padding: 0;
  background-color: var(--background);
  color: var(--text);
}

#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.container {
  display: flex;
  flex: 1;
  height: calc(100vh - 60px);
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
  width: 100%;
  max-width: 1440px;
}

.admin-container {
  max-width: 100%;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  background: #ffffff;
  min-width: 0;
  max-width: calc(100% - 250px);
}

.main-content:only-child {
  max-width: 100%;
}

.admin-content {
  max-width: 100%;
}
</style>
